import { forwardRef, useEffect, useRef } from "react";
import dayjs from "dayjs";
import flatpickr from "flatpickr";
import "flatpickr/dist/themes/light.css";

/** What the user reads. The app is German, so this is not configurable. */
const DISPLAY = "d.m.Y";

/**
 * The one date control. Owns a flatpickr instance per input, bound by ref and
 * destroyed on unmount, so no two screens can reinitialise each other's picker.
 *
 * Accepts either a `register()` spread or a `value` / `onChange` pair. Both are
 * the same two props, so there is one code path here, not two modes. `onChange`
 * is always called with an event-like object, which is what `register()`'s
 * handler expects and what react-hook-form's own Controller builds internally.
 *
 * Uncontrolled at the DOM level on purpose: React and flatpickr must not both
 * write `input.value`, or they fight over the caret while the user types.
 */
const DateInput = forwardRef(function DateInput(
  {
    value,
    onChange,
    onBlur,
    name,
    valueFormat = "DD.MM.YYYY",
    required = false,
    min,
    error = false,
    disabled = false,
    readOnly = false,
    className = "",
    testId,
    ...rest
  },
  ref
) {
  const inputRef = useRef(null);
  const fpRef = useRef(null);

  // A register() spread hands us a callback ref; flatpickr needs the same node.
  const attach = (node) => {
    inputRef.current = node;
    if (typeof ref === "function") ref(node);
    else if (ref) ref.current = node;
  };

  // Effects below run once, so everything they read goes through a ref.
  const stateRef = useRef(null);
  stateRef.current = { value, valueFormat, required, onChange, name, controlled: value !== undefined };

  const toDay = (raw) => (raw ? dayjs(raw, stateRef.current.valueFormat) : null);

  const report = (text) => {
    const { onChange: handler, name: field, value: held } = stateRef.current;
    if (!handler) return;
    // Saying nothing changed still costs a render, and a render between the
    // blur and the selection a calendar click makes would undo the pick.
    if ((text || "") === (held || "")) return;
    handler({ target: { name: field, value: text } });
  };

  /** Push the value the form holds back into the box. */
  const showRef = useRef(null);
  showRef.current = () => {
    const fp = fpRef.current;
    if (!fp) return;

    const { value: held } = stateRef.current;
    if (!held) {
      // Only empty a box that has something in it. Clearing unconditionally
      // would also wipe a selection the picker is in the middle of making.
      if (inputRef.current?.value) fp.clear(false);
      return;
    }

    const day = toDay(held);
    if (!day?.isValid()) return;

    fp.setDate(day.toDate(), false);
    // setDate refuses a day outside minDate, but the box may not go blank over
    // a day the form still holds, so write the text in directly.
    if (!fp.selectedDates.length && inputRef.current) {
      inputRef.current.value = fp.formatDate(day.toDate(), DISPLAY);
    }
  };

  /** Reconcile the box and the form. Runs on a calendar pick and on blur. */
  const syncRef = useRef(null);
  syncRef.current = () => {
    const fp = fpRef.current;
    if (!fp) return;

    const { valueFormat: format, required: mustHold, controlled } = stateRef.current;
    const picked = fp.selectedDates[0];
    if (picked) {
      report(dayjs(picked).format(format));
      return;
    }

    // Text flatpickr could not read. Leave it for validation to report, unless
    // the field may not sit empty over a form that still holds a day.
    if (inputRef.current?.value) {
      if (mustHold && controlled) showRef.current();
      return;
    }

    if (mustHold && controlled) showRef.current();
    else report("");
  };

  useEffect(() => {
    const input = inputRef.current;
    if (!input) return undefined;

    const sync = () => syncRef.current();

    fpRef.current = flatpickr(input, {
      dateFormat: DISPLAY,
      allowInput: true,
      clickOpens: !(disabled || readOnly),
      onChange: sync,
      // An uncontrolled field's value can be written straight to the DOM from
      // outside, by react-hook-form's reset() when the screen's data arrives.
      // Re-read it here so the calendar opens on the day the box shows.
      onOpen: () => {
        if (stateRef.current.controlled) return;
        const fp = fpRef.current;
        const shown = fp.selectedDates[0] ? fp.formatDate(fp.selectedDates[0], DISPLAY) : "";
        if (input.value !== shown) fp.setDate(input.value, false, DISPLAY);
      },
    });

    input.addEventListener("blur", sync);

    return () => {
      input.removeEventListener("blur", sync);
      fpRef.current?.destroy();
      fpRef.current = null;
    };
    // Built once. Rebuilding it mid-life would run destroy(), which strips the
    // readonly attribute off the input and never puts it back.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Runs after the effect above, on mount and on every rebuild of it.
  useEffect(() => {
    fpRef.current?.set("clickOpens", !(disabled || readOnly));
    // flatpickr's destroy() strips the readonly attribute off the input and
    // never puts it back, so React's value for it is restored here.
    if (inputRef.current) inputRef.current.readOnly = readOnly;
  }, [disabled, readOnly]);

  useEffect(() => {
    const day = toDay(min);
    fpRef.current?.set("minDate", day?.isValid() ? day.toDate() : null);
    // Setting minDate can drop the current selection, so put it back on screen.
    if (stateRef.current.controlled) showRef.current();
  }, [min]);

  useEffect(() => {
    if (stateRef.current.controlled) showRef.current();
  }, [value, valueFormat]);

  const classes = ["peb-input", "peb-input--date", error ? "peb-input--error" : "", className]
    .filter(Boolean)
    .join(" ");

  return (
    <input
      ref={attach}
      type="text"
      autoComplete="off"
      name={name}
      className={classes}
      disabled={disabled}
      readOnly={readOnly}
      defaultValue={value ?? ""}
      onBlur={onBlur}
      // Typing is reported by react-hook-form's own handler on a register()
      // spread. A controlled consumer hears it at blur, so that re-rendering
      // mid-word cannot clear the box under the caret.
      onChange={stateRef.current.controlled ? undefined : onChange}
      data-testid={testId}
      {...rest}
    />
  );
});

export default DateInput;
